import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, TrendingUp, DollarSign, Users, Target, Clock,
  ChevronDown, ChevronUp, Sparkles, Download, Filter,
  ArrowUp, ArrowDown, AlertTriangle, CheckCircle, FileSpreadsheet, FileText,
  MessageCircle, Bot, Activity, Mail, Send, Eye, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { exportAnalyticsReport } from '../utils/export';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../components/InfoTip';

const statusLabels = {
  new: 'Nuevos', contacted: 'Contactados', qualified: 'Calificados',
  negotiation: 'Negociación', won: 'Ganados', lost: 'Perdidos', inquiry: 'Consultas'
};

const periodLabels = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual', quarterly: 'Trimestral' };

const ExpandableCard = ({ title, icon: Icon, iconColor, children, defaultExpanded = false, metric, metricLabel }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Card className="glass-card overflow-hidden transition-all duration-300">
      <div
        className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", iconColor || "bg-primary/10")}>
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{title}</p>
              {metric !== undefined && <p className="text-2xl font-bold">{metric}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {metricLabel && <Badge variant="outline" className="text-xs">{metricLabel}</Badge>}
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border p-4 animate-expand-in">
          {children}
        </div>
      )}
    </Card>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdvancedAnalytics = () => {
  const { customers, campaigns, chats, products } = useApp();
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const filteredCustomers = selectedCampaign === 'all' 
    ? customers 
    : customers.filter(c => c.campaign_id === selectedCampaign);

  // Stats
  const totalCustomers = filteredCustomers.length;
  const wonCustomers = filteredCustomers.filter(c => c.status === 'won').length;
  const lostCustomers = filteredCustomers.filter(c => c.status === 'lost').length;
  const activeCustomers = filteredCustomers.filter(c => !['won', 'lost'].includes(c.status)).length;
  const totalRevenue = filteredCustomers.filter(c => c.status === 'won').reduce((s, c) => s + (c.value || 0), 0);
  const totalPotential = filteredCustomers.filter(c => !['won', 'lost'].includes(c.status)).reduce((s, c) => s + (c.value || 0), 0);
  const conversionRate = totalCustomers > 0 ? ((wonCustomers / totalCustomers) * 100).toFixed(1) : 0;
  const avgDealValue = wonCustomers > 0 ? (totalRevenue / wonCustomers).toFixed(0) : 0;

  // Status distribution
  const statusDistribution = Object.entries(statusLabels).map(([key, label]) => ({
    status: key,
    label,
    count: filteredCustomers.filter(c => c.status === key).length,
    value: filteredCustomers.filter(c => c.status === key).reduce((s, c) => s + (c.value || 0), 0)
  }));

  // Campaign breakdown
  const campaignBreakdown = campaigns.map(camp => {
    const campCustomers = customers.filter(c => c.campaign_id === camp.id);
    const won = campCustomers.filter(c => c.status === 'won');
    return {
      ...camp,
      totalCustomers: campCustomers.length,
      wonCount: won.length,
      revenue: won.reduce((s, c) => s + (c.value || 0), 0),
      convRate: campCustomers.length > 0 ? ((won.length / campCustomers.length) * 100).toFixed(1) : 0
    };
  });

  const fetchAiInsights = useCallback(async () => {
    setLoadingInsights(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: selectedCampaign === 'all' ? null : selectedCampaign,
          time_period: timePeriod,
          include_ai_insights: true
        })
      });
      const data = await res.json();
      setAiInsights(data);
    } catch (err) {
      console.warn('AI insights fetch failed:', err?.message);
    } finally {
      setLoadingInsights(false);
    }
  }, [timePeriod, selectedCampaign]);

  useEffect(() => {
    fetchAiInsights();
  }, [fetchAiInsights]);

  // Simple bar for visualization
  const BarVisual = ({ value, max, color = 'bg-primary' }) => (
    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
      <div
        className={cn("h-full rounded-full transition-all duration-700", color)}
        style={{ width: `${max > 0 ? (value / max * 100) : 0}%` }}
      />
    </div>
  );

  return (
    <div data-testid="analytics-page" className="min-h-screen">
      <Header title="Analytics" subtitle="Análisis avanzado por campaña y periodo">
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-analytics-excel"
            variant="outline"
            size="sm"
            className="gap-1 h-9"
            onClick={() => { if (aiInsights) { exportAnalyticsReport(aiInsights, 'excel'); toast.success('Excel exportado'); } }}
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button
            data-testid="export-analytics-pdf"
            variant="outline"
            size="sm"
            className="gap-1 h-9"
            onClick={() => { if (aiInsights) { exportAnalyticsReport(aiInsights, 'pdf'); toast.success('PDF exportado'); } }}
          >
            <FileText className="w-4 h-4" /> PDF
          </Button>
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-36 h-9" data-testid="period-selector">
              <Clock className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(periodLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
            <SelectTrigger className="w-44 h-9" data-testid="campaign-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las campañas</SelectItem>
              {campaigns.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Header>

      <div className="p-6 space-y-6">
        {/* Time period indicator */}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            <Clock className="w-3 h-3 mr-1" />
            Periodo: {periodLabels[timePeriod]}
          </Badge>
          {selectedCampaign !== 'all' && (
            <Badge variant="outline" className="text-sm" style={{ 
              borderColor: campaigns.find(c => c.id === selectedCampaign)?.color 
            }}>
              {campaigns.find(c => c.id === selectedCampaign)?.name}
            </Badge>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ExpandableCard
            title="Clientes Totales"
            icon={Users}
            iconColor="bg-brand-green/10"
            metric={totalCustomers}
            metricLabel={`${activeCustomers} activos`}
          >
            <div className="space-y-2">
              {statusDistribution.map(s => (
                <div key={s.status} className="flex items-center justify-between text-sm">
                  <span>{s.label}</span>
                  <span className="font-medium">{s.count}</span>
                </div>
              ))}
            </div>
          </ExpandableCard>

          <ExpandableCard
            title="Ingresos"
            icon={DollarSign}
            iconColor="bg-emerald-500/10"
            metric={`$${totalRevenue.toLocaleString()}`}
            metricLabel={`Potencial: $${totalPotential.toLocaleString()}`}
          >
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Ingresos confirmados</span>
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">${totalRevenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Pipeline potencial</span>
                <span className="font-medium">${totalPotential.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Ticket promedio</span>
                <span className="font-medium">${Number(avgDealValue).toLocaleString()}</span>
              </div>
            </div>
          </ExpandableCard>

          <ExpandableCard
            title="Tasa de Conversión"
            icon={Target}
            iconColor="bg-violet-500/10"
            metric={`${conversionRate}%`}
            metricLabel={`${wonCustomers} ganados`}
          >
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Ganados</span>
                  <span className="text-emerald-700 dark:text-emerald-400">{wonCustomers}</span>
                </div>
                <BarVisual value={wonCustomers} max={totalCustomers} color="bg-emerald-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Perdidos</span>
                  <span className="text-red-600 dark:text-red-400">{lostCustomers}</span>
                </div>
                <BarVisual value={lostCustomers} max={totalCustomers} color="bg-red-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>En proceso</span>
                  <span>{activeCustomers}</span>
                </div>
                <BarVisual value={activeCustomers} max={totalCustomers} color="bg-brand-green" />
              </div>
            </div>
          </ExpandableCard>

          <ExpandableCard
            title="Rendimiento IA"
            icon={Sparkles}
            iconColor="bg-fuchsia-500/10"
            metric={chats.length}
            metricLabel="Conversaciones"
          >
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Chats con IA (Noah)</span>
                <span className="font-medium">{chats.filter(c => c.messages?.some(m => m.sender === 'ai')).length}</span>
              </div>
              <div className="flex justify-between">
                <span>Respuestas automáticas</span>
                <span className="font-medium">{chats.reduce((s, c) => s + (c.messages?.filter(m => m.sender === 'ai').length || 0), 0)}</span>
              </div>
              <div className="flex justify-between">
                <span>Productos</span>
                <span className="font-medium">{products.length}</span>
              </div>
            </div>
          </ExpandableCard>
        </div>

        {/* Campaign Performance Table */}
        <ExpandableCard
          title="Rendimiento por Campaña"
          icon={BarChart3}
          iconColor="bg-orange-500/10"
          defaultExpanded={true}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2">Campaña</th>
                  <th className="text-right py-3 px-2">Clientes</th>
                  <th className="text-right py-3 px-2">Ganados</th>
                  <th className="text-right py-3 px-2">Conversión</th>
                  <th className="text-right py-3 px-2">Ingresos</th>
                  <th className="text-right py-3 px-2">Progreso</th>
                </tr>
              </thead>
              <tbody>
                {campaignBreakdown.map(camp => (
                  <tr key={camp.id} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: camp.color }} />
                        <span className="font-medium">{camp.name}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-2">{camp.totalCustomers}</td>
                    <td className="text-right py-3 px-2 text-emerald-700 dark:text-emerald-400 font-medium">{camp.wonCount}</td>
                    <td className="text-right py-3 px-2">{camp.convRate}%</td>
                    <td className="text-right py-3 px-2 font-medium">${camp.revenue.toLocaleString()}</td>
                    <td className="py-3 px-2 w-32">
                      <BarVisual value={camp.wonCount} max={camp.totalCustomers || 1} color="bg-emerald-500" />
                    </td>
                  </tr>
                ))}
                {campaignBreakdown.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-6 text-muted-foreground">Sin campañas</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ExpandableCard>

        {/* Status Funnel */}
        <ExpandableCard
          title="Embudo de Ventas"
          icon={TrendingUp}
          iconColor="bg-brand-green/10"
          defaultExpanded={true}
        >
          <div className="space-y-3">
            {statusDistribution.filter(s => s.count > 0).map((s, idx) => {
              const maxCount = Math.max(...statusDistribution.map(sd => sd.count), 1);
              const width = (s.count / maxCount * 100);
              return (
                <div key={s.status} className="flex items-center gap-4">
                  <span className="w-28 text-sm text-muted-foreground">{s.label}</span>
                  <div className="flex-1">
                    <div className="h-8 bg-muted rounded-lg overflow-hidden relative">
                      <div
                        className="h-full rounded-lg transition-all duration-700 flex items-center px-3"
                        style={{
                          width: `${Math.max(width, 10)}%`,
                          backgroundColor: s.status === 'won' ? '#0f7a52'
                            : s.status === 'lost' ? '#c62828'
                            : s.status === 'inquiry' ? '#2d5b45'
                            : '#002d2c'
                        }}
                      >
                        <span className="text-white text-xs font-medium">{s.count}</span>
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-medium w-24 text-right">${s.value.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </ExpandableCard>

        {/* Noah AI Insights */}
        <ExpandableCard
          title="Análisis de Noah IA"
          icon={Sparkles}
          iconColor="bg-primary/10"
          defaultExpanded={true}
        >
          {loadingInsights ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Sparkles className="w-5 h-5 animate-spin" />
                <p>Noah está analizando tus datos para generar recomendaciones</p>
              </div>
            </div>
          ) : aiInsights?.ai_analysis ? (
            <div className="space-y-4">
              {/* SWOT */}
              {aiInsights.ai_analysis.swot && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(aiInsights.ai_analysis.swot).map(([key, items]) => (
                    <div key={key} className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        {key === 'strengths' ? 'Fortalezas' : key === 'weaknesses' ? 'Debilidades' : key === 'opportunities' ? 'Oportunidades' : 'Amenazas'}
                      </p>
                      <ul className="space-y-1">
                        {(Array.isArray(items) ? items : [items]).map((item, i) => (
                          <li key={`${key}-${i}-${String(item).slice(0,20)}`} className="text-sm flex items-start gap-2">
                            <span className="mt-1">
                              {key === 'strengths' ? <CheckCircle className="w-3 h-3 text-emerald-700 dark:text-emerald-400" /> 
                                : key === 'weaknesses' ? <AlertTriangle className="w-3 h-3 text-amber-700 dark:text-amber-400" />
                                : key === 'opportunities' ? <ArrowUp className="w-3 h-3 text-brand-deep dark:text-brand-green" />
                                : <ArrowDown className="w-3 h-3 text-red-600 dark:text-red-400" />}
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations */}
              {aiInsights.ai_analysis.recommendations && (
                <div>
                  <p className="text-sm font-semibold mb-2">Recomendaciones de Noah</p>
                  <div className="space-y-2">
                    {(Array.isArray(aiInsights.ai_analysis.recommendations) 
                      ? aiInsights.ai_analysis.recommendations 
                      : [aiInsights.ai_analysis.recommendations]
                    ).map((rec, i) => (
                      <div key={`rec-${i}-${String(rec).slice(0,20)}`} className="flex items-start gap-2 text-sm p-2 bg-violet-500/5 rounded-lg">
                        <Sparkles className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green mt-0.5 flex-shrink-0" />
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No hay insights disponibles todavía</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={fetchAiInsights}>
                Generar análisis
              </Button>
            </div>
          )}
        </ExpandableCard>

        {/* Clients by Status - Expandable lists */}
        <ExpandableCard
          title="Clientes por Estado"
          icon={Users}
          iconColor="bg-brand-green/10"
          defaultExpanded={false}
        >
          <Tabs defaultValue="new" className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto flex-nowrap">
              {statusDistribution.filter(s => s.count > 0).map(s => (
                <TabsTrigger key={s.status} value={s.status} className="text-xs">
                  {s.label} ({s.count})
                </TabsTrigger>
              ))}
            </TabsList>
            {statusDistribution.map(s => (
              <TabsContent key={s.status} value={s.status}>
                <ScrollArea className="max-h-60">
                  <div className="space-y-1">
                    {filteredCustomers.filter(c => c.status === s.status).map(cust => (
                      <div key={cust.id} className="flex items-center justify-between p-2.5 bg-muted/20 rounded-lg hover:bg-muted/40 transition-colors">
                        <div>
                          <p className="text-sm font-medium">{cust.name}</p>
                          <p className="text-xs text-muted-foreground">{cust.email || cust.phone || 'Sin contacto'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{cust.value ? `$${cust.value.toLocaleString()}` : '-'}</p>
                          <p className="text-xs text-muted-foreground">{cust.source || 'Direct'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        </ExpandableCard>

        {/* Monitor Performance Dashboard */}
        <MonitorDashboard />

        {/* Noah Weekly Email Config */}
        <NoahEmailConfig />
      </div>
    </div>
  );
};

// ==================== MONITOR DASHBOARD ====================
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MonitorDashboard = () => {
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/crm/performance-summary`);
        setPerf(res.data);
      } catch (err) { console.warn('Performance fetch error:', err?.message); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading || !perf) return null;

  const stats = [
    { label: 'Mensajes Totales', value: perf.total_messages, icon: MessageCircle, color: 'text-brand-deep dark:text-brand-green', to: '/inbox', hint: 'Ver el Inbox unificado',
      info: { title: 'Mensajes totales', text: 'Todos los mensajes intercambiados en tus campañas, sumando los de Noah, los de tu equipo y los de los clientes.', source: 'Mensajes de todos los chats del workspace', to: '/inbox', linkLabel: 'Abrir Inbox' } },
    { label: 'Respuestas Noah', value: perf.ai_messages, icon: Bot, color: 'text-brand-deep dark:text-brand-deep dark:text-brand-green', to: '/inbox', hint: 'Ver conversaciones de Noah',
      info: { title: 'Respuestas de Noah', text: 'Mensajes escritos por la IA. Si una campaña está en modo manual, Noah no responde y este número no sube.', source: 'Mensajes con remitente Noah', to: '/campaigns', linkLabel: 'Ver modo de Noah por campaña' } },
    { label: 'Mensajes Agente', value: perf.agent_messages, icon: Send, color: 'text-emerald-700 dark:text-emerald-400', to: '/inbox', hint: 'Ver chats con humano',
      info: { title: 'Mensajes de agente', text: 'Lo que escribió tu equipo tras tomar el control de un chat (takeover).', source: 'Mensajes con remitente humano', to: '/inbox', linkLabel: 'Ver chats con humano' } },
    { label: 'Mensajes Cliente', value: perf.customer_messages, icon: Users, color: 'text-amber-700 dark:text-amber-400', to: '/customers', hint: 'Ver clientes',
      info: { title: 'Mensajes de clientes', text: 'Mensajes entrantes desde cualquier canal conectado. Cada uno puede disparar tus automatizaciones.', source: 'Mensajes entrantes de los webhooks de canal', to: '/automations', linkLabel: 'Ver automatizaciones' } },
  ];

  const ProgressRing = ({ value, max = 100, size = 80, color = '#559975' }) => {
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (Math.min(value, max) / max) * circumference;
    return (
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="currentColor" strokeWidth="4" fill="none" className="text-muted/30" />
        <circle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth="4" fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round"
          className="transition-all duration-1000" />
        <text x={size / 2} y={size / 2} textAnchor="middle" dy="0.35em" className="fill-foreground text-sm font-bold" transform={`rotate(90, ${size / 2}, ${size / 2})`}>
          {value}%
        </text>
      </svg>
    );
  };

  return (
    <ExpandableCard title="Monitor de Rendimiento" icon={Activity} iconColor="bg-brand-green/10" defaultExpanded={true}>
      <div className="space-y-6">
        {/* Message Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.map(s => (
            <div key={s.label} className="relative">
              <button onClick={() => navigate(s.to)} title={s.hint}
                data-testid={`monitor-stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}
                className="group w-full p-3 bg-muted/20 rounded-xl text-center transition-[background-color,box-shadow] hover:bg-muted/40 hover:shadow-lg hover:shadow-primary/5">
                <s.icon className={cn("w-5 h-5 mx-auto mb-1", s.color)} />
                <p className="text-xl font-bold">{s.value}</p>
                <p className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                  {s.label} <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-70 transition-opacity" />
                </p>
              </button>
              {s.info && <div className="absolute top-1.5 right-1.5"><InfoTip {...s.info} testId={`monitor-info-${s.label.replace(/\s+/g, '-').toLowerCase()}`} /></div>}
            </div>
          ))}
        </div>

        {/* Performance Rings */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button onClick={() => navigate('/reports')} title="Ver reportes por campaña"
            data-testid="monitor-ring-conversion"
            className="group flex flex-col items-center p-4 bg-muted/10 rounded-xl transition-[background-color] hover:bg-muted/30">
            <ProgressRing value={perf.conversion_rate} color="#10b981" />
            <p className="text-sm font-medium mt-2 inline-flex items-center gap-1">
              Tasa de Conversion <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
            </p>
            <p className="text-xs text-muted-foreground">{perf.won} de {perf.total_customers} clientes</p>
          </button>
          <button onClick={() => navigate('/inbox')} title="Ver el Inbox de Noah"
            data-testid="monitor-ring-noah-ratio"
            className="group flex flex-col items-center p-4 bg-muted/10 rounded-xl transition-[background-color] hover:bg-muted/30">
            <ProgressRing value={perf.ai_response_ratio} color="#559975" />
            <p className="text-sm font-medium mt-2 inline-flex items-center gap-1">
              Ratio Respuesta Noah <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
            </p>
            <p className="text-xs text-muted-foreground">{perf.ai_messages} de {perf.customer_messages} mensajes</p>
          </button>
          <button onClick={() => navigate('/deals')} title="Ver Gestión Noah y negocios cerrados"
            data-testid="monitor-ring-effectiveness"
            className="group flex flex-col items-center p-4 bg-muted/10 rounded-xl transition-[background-color] hover:bg-muted/30">
            <ProgressRing value={Math.min(perf.noah_effectiveness, 100)} color="#f59e0b" />
            <p className="text-sm font-medium mt-2 inline-flex items-center gap-1">
              Efectividad Noah <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
            </p>
            <p className="text-xs text-muted-foreground">Ventas cerradas por IA</p>
          </button>
        </div>

        {/* Summary Bar — cada dato enlaza a la pantalla de donde sale la información */}
        <div className="p-3 bg-primary/10 rounded-xl border border-violet-500/20" data-testid="noah-summary-bar">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
            <span className="text-sm font-semibold">Resumen Noah</span>
            <button onClick={() => navigate('/reports')} data-testid="noah-summary-reports-link"
              className="ml-auto text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1">
              Ver reporte completo <ExternalLink className="w-3 h-3" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <button onClick={() => navigate('/inbox')} data-testid="noah-summary-chats-link"
              className="underline decoration-dotted decoration-violet-500/50 hover:text-brand-deep dark:text-brand-deep dark:text-brand-green transition-colors inline-flex items-center gap-1">
              {perf.active_chats} chats activos <ExternalLink className="w-3 h-3" />
            </button>
            <span className="text-muted-foreground">|</span>
            <button onClick={() => navigate('/deals')} data-testid="noah-summary-won-link"
              className="text-emerald-700 dark:text-emerald-400 underline decoration-dotted decoration-emerald-500/50 hover:text-emerald-700 dark:text-emerald-400 transition-colors inline-flex items-center gap-1">
              {perf.won} ventas cerradas <ExternalLink className="w-3 h-3" />
            </button>
            <span className="text-muted-foreground">|</span>
            <button onClick={() => navigate('/customers')} data-testid="noah-summary-lost-link"
              className="text-red-600 dark:text-red-400 underline decoration-dotted decoration-red-500/50 hover:text-red-400 transition-colors inline-flex items-center gap-1">
              {perf.lost} perdidos <ExternalLink className="w-3 h-3" />
            </button>
            <span className="text-muted-foreground">|</span>
            <button onClick={() => navigate('/tickets')} data-testid="noah-summary-tickets-link"
              className="text-amber-700 dark:text-amber-400 underline decoration-dotted decoration-amber-500/50 hover:text-amber-700 dark:text-amber-400 transition-colors inline-flex items-center gap-1">
              Tickets abiertos <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </ExpandableCard>
  );
};

// ==================== NOAH WEEKLY EMAIL CONFIG ====================
const NoahEmailConfig = () => {
  const [config, setConfig] = useState({ enabled: false, email: '', frequency: 'weekly' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/noah/email-report/config`);
        setConfig(res.data);
      } catch { /* ignore */ }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/noah/email-report/config`, config);
      toast.success('Configuracion guardada');
    } catch { toast.error('Error al guardar'); }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/noah/email-report/send-now`);
      if (res.data.status === 'sent') {
        toast.success(`Resumen enviado a ${res.data.email}`);
      } else {
        toast.error(res.data.error || 'Error al enviar');
      }
    } catch (err) { toast.error('Error al enviar reporte'); }
    finally { setSending(false); }
  };

  return (
    <ExpandableCard title="Resumen Semanal por Email" icon={Mail} iconColor="bg-brand-copper/10" defaultExpanded={false}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Activar resumen de Noah por email</p>
            <p className="text-xs text-muted-foreground">Noah enviara un resumen con las metricas clave de tu CRM</p>
          </div>
          <Switch data-testid="email-report-toggle" checked={config.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} />
        </div>
        {config.enabled && (
          <>
            <div>
              <Label className="text-xs">Email destinatario</Label>
              <Input value={config.email || ''} onChange={e => setConfig(c => ({ ...c, email: e.target.value }))}
                placeholder="tu@correo.com" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Frecuencia</Label>
              <Select value={config.frequency} onValueChange={v => setConfig(c => ({ ...c, frequency: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="biweekly">Quincenal</SelectItem>
                  <SelectItem value="monthly">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} className="bg-primary">Guardar</Button>
              <Button size="sm" variant="outline" onClick={handleSendNow} disabled={sending} className="gap-1">
                <Send className="w-3.5 h-3.5" /> {sending ? 'Enviando...' : 'Enviar ahora'}
              </Button>
            </div>
          </>
        )}
      </div>
    </ExpandableCard>
  );
};

export default AdvancedAnalytics;
